import { MutableRefObject } from 'react';
import { Socket } from 'socket.io-client';
import { ChatMessage, SocketChatSession } from '../types/chat';
import { SocketMessage, SocketType } from '../types/socket';
import { ScreenType } from '../types/common';
import { UserRole } from '../types/user';
import { UserHelper } from './UserHelper';

const READ_EMIT_DEBOUNCE_MS = 1000;

export class ReadReceiptHelper {
  static isUnreadFromCounterparty(message: ChatMessage, screenType: ScreenType): boolean {
    if (message.read) {
      return false;
    }

    if (screenType === ScreenType.Agent) {
      return message.sender?.role === UserRole.Client;
    }

    return UserHelper.isAgentUserRole(message.sender?.role);
  }

  static countUnreadMessages(messages: ChatMessage[], screenType: ScreenType): number {
    return messages.filter(message => ReadReceiptHelper.isUnreadFromCounterparty(message, screenType)).length;
  }

  static findFirstUnreadMessageId(messages: ChatMessage[], screenType: ScreenType): string | null {
    const firstUnread = messages.find(message => ReadReceiptHelper.isUnreadFromCounterparty(message, screenType));
    return firstUnread?.id ?? null;
  }

  static applyReadTimestamps(messages: ChatMessage[], idsToMark: string[], readAt: Date): ChatMessage[] {
    if (idsToMark.length === 0) {
      return messages;
    }

    const idsSet = new Set(idsToMark);
    return messages.map(message => (
      idsSet.has(message.id) ? { ...message, read: readAt } : message
    ));
  }

  static getUnreadCounterpartyMessages(messages: ChatMessage[], screenType: ScreenType): ChatMessage[] {
    return messages.filter(message => ReadReceiptHelper.isUnreadFromCounterparty(message, screenType));
  }
}

export const createReadReceiptEmitter = (
  getSession: () => { session: SocketChatSession | null; getServerNow: () => Date }
) => {
  const pendingMessages = new Map<string, ChatMessage>();
  let emitTimeoutId: ReturnType<typeof setTimeout> | null = null;

  const flush = (socket: MutableRefObject<Socket | null>) => {
    if (emitTimeoutId) {
      clearTimeout(emitTimeoutId);
      emitTimeoutId = null;
    }

    if (!socket.current?.connected || pendingMessages.size === 0) {
      pendingMessages.clear();
      return;
    }

    const { session, getServerNow } = getSession();
    const readMessages = Array.from(pendingMessages.values());
    pendingMessages.clear();

    const readMessage: SocketMessage = {
      session: session || null,
      readMessages,
      socket: SocketType.READ,
      senderId: session?.user?.id,
      timestamp: getServerNow()
    };

    socket.current.emit('message', readMessage);
  };

  const queue = (socket: MutableRefObject<Socket | null>, messages: ChatMessage[]) => {
    messages.forEach(message => pendingMessages.set(message.id, message));

    if (emitTimeoutId) {
      clearTimeout(emitTimeoutId);
    }

    emitTimeoutId = setTimeout(() => flush(socket), READ_EMIT_DEBOUNCE_MS);
  };

  const dispose = (socket: MutableRefObject<Socket | null>) => {
    flush(socket);
  };

  return { queue, flush, dispose };
};
